import React, { Suspense } from 'react';
import { Toaster as ShadToaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { usePageTracking } from "./hooks/usePageTracking";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import UTMTracker from "@/components/UTMTracker";
import AuthRedirect from "./components/auth/AuthRedirect";
import AuthHashRedirect from "./components/auth/AuthHashRedirect";
import LoadingSpinner from "./components/LoadingSpinner";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// i18n instance for provider
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';

// Eagerly loaded (landing + auth - needed immediately)
import Index from "./pages/Index";
import ClientAuth from "./pages/ClientAuth";
import PractitionerAuth from "./pages/PractitionerAuth";

// Lazy loaded pages
const BecomePractitioner = React.lazy(() => import("./pages/BecomePractitioner"));
const Practitioners = React.lazy(() => import("./pages/Practitioners"));
const BookingDatetime = React.lazy(() => import("./pages/BookingDateTime"));
const Payment = React.lazy(() => import("./pages/Payment"));
const BookingSuccess = React.lazy(() => import("./pages/BookingSuccess"));
const PractitionerDashboard = React.lazy(() => import("./pages/PractitionerDashboard"));
const AdminLogin = React.lazy(() => import("./pages/admin/AdminLogin"));
const AdminRoutes = React.lazy(() => import("./routes/adminRoutes"));
const PaymentCancelled = React.lazy(() => import("./pages/PaymentCancelled"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const ClientDashboard = React.lazy(() => import("./pages/ClientDashboard"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const UpdatePassword = React.lazy(() => import("./pages/UpdatePassword"));
const NewsEvents = React.lazy(() => import("./pages/NewsEvents"));
const NewsArticle = React.lazy(() => import("./pages/NewsArticle"));
const CertificationsList = React.lazy(() => import("./pages/CertificationsList"));
const TherapyWikipedia = React.lazy(() => import("./pages/TherapyWikipedia"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const ZoomOAuthCallback = React.lazy(() => import("./pages/ZoomOAuthCallback"));
const GoogleCalendarOAuthCallback = React.lazy(() => import("./pages/GoogleCalendarOAuthCallback"));
const PractitionerDetail = React.lazy(() => import("./components/practitioners/PractitionerDetail"));
const SessionToolsPage = React.lazy(() => import("./pages/SessionToolsPage"));
const SmsTest = React.lazy(() => import("./pages/SmsTest"));
const CareersPage = React.lazy(() => import("./pages/CareersPage"));
const AccessibilityPage = React.lazy(() => import("./pages/AccessibilityPage"));
const BookingChoice = React.lazy(() => import("./pages/BookingChoice"));
const CrisisResources = React.lazy(() => import("./pages/CrisisResources"));
const ServiceSelection = React.lazy(() => import("./pages/ServiceSelection"));
const ReferralDebugPage = React.lazy(() => import("./pages/ReferralDebugPage"));
const Events = React.lazy(() => import("./pages/Events"));
const EventDetail = React.lazy(() => import("./pages/EventDetail"));
const SmsConsent = React.lazy(() => import("./pages/SmsConsent"));
const StudioManagerPage = React.lazy(() => import("./pages/StudioManagerPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const ConsciousnessDemo = React.lazy(() => import("./pages/ConsciousnessDemo"));

// Space rental MVP pages
const SpaceLanding = React.lazy(() => import("./pages/space/SpaceLanding"));
const SpaceAuth = React.lazy(() => import("./pages/space/SpaceAuth"));
const SpaceDashboard = React.lazy(() => import("./pages/space/SpaceDashboard"));
const SpaceBrowse = React.lazy(() => import("./pages/space/SpaceBrowse"));
const SpaceDetail = React.lazy(() => import("./pages/space/SpaceDetail"));

// SessionFunctionPage uses named export, wrap it
const SessionFunctionPage = React.lazy(() => 
  import("./components/session").then(mod => ({ default: mod.SessionFunctionPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30s global default
    },
  },
});

function AppWithProviders() {
  // React version logged once in dev only
  if (import.meta.env.DEV) console.debug('React', React.version);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <div className="min-h-screen w-full">
                <div className="min-h-screen pb-safe">
                  <BrowserRouter>
                    <ErrorBoundary>
                      {mounted && <ShadToaster />}
                      <AppRoutes />
                    </ErrorBoundary>
                  </BrowserRouter>
                </div>
              </div>
            </AuthProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  usePageTracking();

  return (
    <>
      <UTMTracker />
      <AuthHashRedirect />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/client-auth" element={<ClientAuth />} />
          <Route path="/signup" element={<ClientAuth />} />
          <Route path="/practitioner-auth" element={<PractitionerAuth />} />
          <Route path="/become-practitioner" element={<BecomePractitioner />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/booking-choice" element={<BookingChoice />} />
          <Route path="/crisis-resources" element={<CrisisResources />} />
          <Route path="/service-selection" element={<ServiceSelection />} />
          <Route path="/book-an-appointment" element={<Practitioners />} />
          <Route path="/book-an-appointment/:id" element={<PractitionerDetail isFullPage={true} />} />
          <Route path="/framework" element={<TherapyWikipedia />} />
          <Route path="/certifications-legacy" element={<CertificationsList />} />
          <Route path="/booking-datetime" element={<BookingDatetime />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/practitioner-dashboard" element={<ProtectedRoute requiredRole={"practitioner"} element={<PractitionerDashboard />} />} />
          <Route path="/client-dashboard" element={<ProtectedRoute requiredRole={"client"} element={<ClientDashboard />} />} />
          <Route path="/session/:bookingId" element={<SessionFunctionPage />} />
          <Route path="/session-tools/:bookingId" element={<ProtectedRoute requiredRole={"practitioner"} element={<SessionToolsPage />} />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/*" element={<ProtectedRoute requiredRole={"admin"} element={<AdminRoutes />} />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/zoom/callback" element={<ZoomOAuthCallback />} />
          <Route path="/oauth/google-calendar/callback" element={<GoogleCalendarOAuthCallback />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/news-events" element={<NewsEvents />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/dashboard" element={<AuthRedirect />} />
          <Route path="/profile" element={<AuthRedirect />} />
          <Route path="/referral-debug" element={<ReferralDebugPage />} />
          <Route path="/sms-test" element={<SmsTest />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/sms-consent" element={<SmsConsent />} />
          <Route path="/studio-manager" element={<ProtectedRoute requiredRole={"practitioner"} element={<StudioManagerPage />} />} />
          <Route path="/certifications" element={<Navigate to="/framework" replace />} />
          <Route path="/consciousness-demo" element={<ConsciousnessDemo />} />
          {/* Space rental MVP */}
          <Route path="/space" element={<SpaceLanding />} />
          <Route path="/space/auth" element={<SpaceAuth />} />
          <Route path="/space/dashboard" element={<ProtectedRoute requiredRole={"host"} element={<SpaceDashboard />} />} />
          <Route path="/space/browse" element={<SpaceBrowse />} />
          <Route path="/space/browse/:id" element={<SpaceDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default AppWithProviders;
